import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { walletAPI } from '../../services/api';

const WithdrawalTest = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testData, setTestData] = useState({
    transactionId: '',
    action: ''
  });

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await walletAPI.getWithdrawals();
      console.log('Withdrawals Response:', response.data);
      
      if (response.data.success) {
        setWithdrawals(response.data.withdrawals || []);
        setSuccess('Withdrawals loaded successfully!');
      } else {
        setError('Failed to fetch withdrawals');
      }
    } catch (err) {
      console.error('Error fetching withdrawals:', err);
      setError(err.response?.data?.message || 'Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleTestAction = async () => {
    try {
      setLoading(true);
      setError('');
      
      let response;
      if (testData.action === 'approve') {
        response = await walletAPI.approveWithdrawal(testData.transactionId);
      } else if (testData.action === 'reject') {
        response = await walletAPI.rejectWithdrawal(testData.transactionId);
      }
      
      console.log('Action Response:', response.data);
      
      if (response.data.success) {
        setSuccess(`Withdrawal ${testData.action}d successfully!`);
        fetchWithdrawals(); // Refresh the list
      } else {
        setError(`Failed to ${testData.action} withdrawal`);
      }
    } catch (err) {
      console.error(`Error ${testData.action}ing withdrawal:`, err);
      setError(err.response?.data?.message || `Failed to ${testData.action} withdrawal`);
    } finally {
      setLoading(false);
      setTestDialogOpen(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'success':
        return 'success';
      case 'rejected':
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Withdrawal Management Test
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={fetchWithdrawals}
          disabled={loading}
          sx={{ mr: 2 }}
        >
          {loading ? 'Loading...' : 'Fetch Withdrawals'}
        </Button>

        <Button
          variant="outlined"
          onClick={() => setTestDialogOpen(true)}
          disabled={loading}
        >
          Test Approve/Reject
        </Button>
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        API Endpoints Being Used:
      </Typography>
      
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" component="pre">
          GET /api/wallet/withdrawals - Fetch all withdrawals{'\n'}
          PUT /api/wallet/withdraw/approve/:transaction_id - Approve withdrawal{'\n'}
          PUT /api/wallet/withdraw/reject/:transaction_id - Reject withdrawal
        </Typography>
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Withdrawals ({withdrawals.length}):
      </Typography>

      {withdrawals.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary">
              No withdrawals found. This could mean:
              <br />
              1. No withdrawal requests have been made yet
              <br />
              2. The API endpoint might not be working
              <br />
              3. Authorization issues
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {withdrawals.map((withdrawal) => (
            <Grid item xs={12} md={6} lg={4} key={withdrawal.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                    <Typography variant="h6">
                      ID: {withdrawal.id}
                    </Typography>
                    <Chip
                      label={withdrawal.status.toUpperCase()}
                      color={getStatusColor(withdrawal.status)}
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    User ID: {withdrawal.from_user_id}
                  </Typography>

                  <Typography variant="h5" color="primary" gutterBottom>
                    {formatCurrency(withdrawal.amount)}
                  </Typography>

                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Created: {formatDate(withdrawal.created_at)}
                  </Typography>

                  {withdrawal.approved_by && (
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {withdrawal.status === 'approved' ? 'Approved' : 'Rejected'} by: {withdrawal.approved_by}
                      <br />
                      On: {formatDate(withdrawal.approved_at)}
                    </Typography>
                  )}

                  {withdrawal.status === 'pending' && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => {
                          setTestData({ transactionId: withdrawal.id, action: 'approve' });
                          setTestDialogOpen(true);
                        }}
                        sx={{ mr: 1 }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => {
                          setTestData({ transactionId: withdrawal.id, action: 'reject' });
                          setTestDialogOpen(true);
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Test Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Test Withdrawal {testData.action === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Transaction ID"
            value={testData.transactionId}
            onChange={(e) => setTestData({ ...testData, transactionId: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Action"
            value={testData.action}
            onChange={(e) => setTestData({ ...testData, action: e.target.value })}
            select
            SelectProps={{ native: true }}
          >
            <option value="">Select Action</option>
            <option value="approve">Approve</option>
            <option value="reject">Reject</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTestAction}
            variant="contained"
            disabled={!testData.transactionId || !testData.action || loading}
          >
            {loading ? 'Processing...' : `${testData.action} Withdrawal`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WithdrawalTest;
